import { useState } from 'react';
import { useGetLmsPreconditionsQuery, useAddLmsPreconditionMutation, useUpdateLmsPreconditionMutation, useDeleteLmsPreconditionMutation } from '../../../../api';
import toastService from '../../../../utils/toastService';
import { formatErrorMessage } from '../../../../utils/errorUtils';
import { useLanguage } from '../../../../contexts/LanguageContext';

/**
 * Ön Koşul Tanımları (LmsPrecondition) ekranı için CRUD veri/state hook'u.
 * Bkz. docs/LMS-Egitim-Katalogu-ve-Egitmenler-API 2.md §6.
 *
 * `code` backend tarafından üretilir ve hiç gönderilmez; ekleme/güncelleme yalnızca
 * `translations` (precondition + description, 6 dilin tamamı veya değişenler) taşır.
 */
const usePreconditionDefinitionsData = () => {
    const { th } = useLanguage();
    const [lazyParams, setLazyParams] = useState({ first: 0, rows: 10, page: 1 });
    const [activeFilters, setActiveFilters] = useState({ code: '', text: '' });

    const {
        data: preconditionApi,
        isLoading,
        isFetching,
        error,
        refetch
    } = useGetLmsPreconditionsQuery(
        {
            code: activeFilters.code || undefined,
            text: activeFilters.text || undefined,
            pageNumber: lazyParams.page,
            pageSize: lazyParams.rows
        },
        { refetchOnMountOrArgChange: true }
    );

    const [addMutation] = useAddLmsPreconditionMutation();
    const [updateMutation] = useUpdateLmsPreconditionMutation();
    const [deleteMutation] = useDeleteLmsPreconditionMutation();

    const saveDefinition = async (formData) => {
        try {
            if (formData.id) {
                await updateMutation(formData).unwrap();
            } else {
                await addMutation(formData).unwrap();
            }
            toastService.success(th('preconditionDefinitions.saveSuccess'), th('common.success'));
            refetch();
            return true;
        } catch (err) {
            toastService.error(formatErrorMessage(err) || th('preconditionDefinitions.saveError'), th('common.error'));
            return false;
        }
    };

    const deleteDefinition = async (id) => {
        await deleteMutation(id).unwrap();
        refetch?.();
    };

    const onPage = (event) => {
        setLazyParams((prev) => ({
            ...prev,
            page: event.page + 1,
            first: event.first,
            rows: event.rows
        }));
    };

    const handleFilterChange = (updated) => {
        setActiveFilters({ code: updated.code || '', text: updated.text || '' });
        setLazyParams((prev) => ({ ...prev, first: 0, page: 1 }));
    };

    const initFilters = () => {
        setActiveFilters({ code: '', text: '' });
        setLazyParams((prev) => ({ ...prev, first: 0, page: 1 }));
    };

    return {
        preconditionApi: preconditionApi || { items: [], totalCount: 0 },
        isLoading,
        isFetching,
        error,
        lazyParams,
        onPage,
        activeFilters,
        handleFilterChange,
        initFilters,
        saveDefinition,
        deleteDefinition,
        refetch
    };
};

export default usePreconditionDefinitionsData;
